import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import Select from "react-select";
import DropDownStyles, {
  DropDownStylesDark,
  defaultSelectPortalProps,
} from "../staff/dropdownStyles";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { useSelector, useDispatch } from "react-redux";
import {
  setVisibleColumns,
  setSortOrder,
  setSortBy,
  setFilterField,
} from "../../app/itemGridPreferencesSlice";
import {
  mergeVisibleColumns,
  needsPreferencesUpdate,
} from "../../utils/preferencesUtils";
import useThemeColor from "../../hooks/useThemeColor";
import AddButton from "../../components/CustomComponents/Buttons/AddButton/AddButton";
import swal from "sweetalert";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import StatusBadge from "../../components/CustomComponents/StatusBadge/StatusBadge";
import useResetPageOnFilterChange from "../../hooks/useResetPageOnFilterChange";
import useDebouncedEffect from "../../hooks/useDebouncedEffect";
import useInvalidateFetchOnFilterChange from "../../hooks/useInvalidateFetchOnFilterChange";
import { applyCatalogTotalFromFetch } from "../../utils/catalogTotalRecords";


import ModernGridToolbar, {
  ModernGridToolbarFilterPill,
} from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ModernGridPagerFooter from "../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter";
import GridEmptyState from "../../components/CustomComponents/ModernGridToolbar/GridEmptyState";
import { getModernGridFilterPillSelectStyles } from "../../components/CustomComponents/ModernGridToolbar/modernGridToolbarSelectStyles";
import SortableColumnHeader from "../../components/CustomComponents/ModernGridToolbar/SortableColumnHeader";
import TableActionsMenu, {
  TableActionsItem,
} from "../../components/CustomComponents/TableActionsMenu/TableActionsMenu";
import ModifierFormModal from "./modifierFormModal";
import "../items/manageItems.scss";

const MODIFIER_GRID_COLUMN_ORDER = [
  "name",
  "costPrice",
  "tax",
  "SalePrice",
  "deliveryPrice",
  "takeAwayPrice",
  "category",
  "stock",
  "status",
];

const MODIFIER_SORT_FIELDS = {
  name: "MenuItem_Name",
  costPrice: "MenuItem_CostPrice",
  tax: "MenuItem_TaxPercentage",
  SalePrice: "MenuItem_SalePrice",
  deliveryPrice: "MenuItem_DeliveryPrice",
  takeAwayPrice: "MenuItem_TakeAwayPrice",
  status: "MenuItem_Publish",
  stock: "Is_OutOfStock",
};

const MODIFIER_SORT_LABELS = {
  MenuItem_Name: "Name",
  MenuItem_CostPrice: "Cost Price",
  MenuItem_TaxPercentage: "Tax",
  MenuItem_SalePrice: "Sale Price",
  MenuItem_DeliveryPrice: "Delivery Price",
  MenuItem_TakeAwayPrice: "Take Away Price",
  MenuItem_Publish: "Status",
  Is_OutOfStock: "Stock",
  MenuItem_Orderby: "Order By",
};

const currencySymbols = {
  GBP: "£",
  USD: "$",
  EUR: "€",
};

const ManageModifiers = () => {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const dispatch = useDispatch();
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const filterPillSelectStyles = useMemo(
    () => getModernGridFilterPillSelectStyles(dropdownStyles, isDarkMode),
    [dropdownStyles, isDarkMode]
  );

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [modifiers, setModifiers] = useState([]);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [catalogTotalRecords, setCatalogTotalRecords] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [compCurrency, setCompCurrency] = useState("GBP");
  const [statusConfirmItem, setStatusConfirmItem] = useState(null);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingModifier, setEditingModifier] = useState(null);
  const lastSelectedIndex = useRef(null);
  const fetchSeqRef = useRef(0);

  const pageKey = "modifiers";
  const defaultVisibleColumns = {
    name: true,
    costPrice: false,
    tax: false,
    SalePrice: true,
    deliveryPrice: false,
    takeAwayPrice: false,
    category: true,
    stock: true,
    status: true,
  };
  const defaultSortOrder = { value: "DESC", label: "DESC" };
  const defaultSortBy = { value: "MenuItem_Orderby", label: "Order By" };
  const defaultStatus = { value: "", label: "All Status" };
  const defaultPageSize = { value: "50", label: "50" };
  const validPageSizes = ["20", "50", "100"];

  const preferences = useSelector(
    (state) => state.itemGridPreferences[pageKey] || {}
  );
  const visibleColumns = mergeVisibleColumns(
    preferences.visibleColumns,
    defaultVisibleColumns
  );
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const status = preferences.status || defaultStatus;
  const pageSize =
    preferences.pageSize &&
    validPageSizes.includes(String(preferences.pageSize.value))
      ? preferences.pageSize
      : defaultPageSize;

  const statusOptions = [
    { value: "", label: "All Status" },
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
  ];

  const statusSelectValue =
    statusOptions.find((o) => String(o.value) === String(status?.value ?? "")) ??
    defaultStatus;

  const getCurrencySymbol = (currencyCode) =>
    currencySymbols[currencyCode] || currencyCode;

  const handlePageSizeChange = useCallback(
    (value) => {
      dispatch(setFilterField({ pageKey, field: "pageSize", value }));
    },
    [dispatch]
  );

  const fetchCompCurrency = useCallback(async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_COMPANY_PROFILE);
      setCompCurrency(response.data.data?.compCurrency || "GBP");
    } catch {
      showErrorToast("Failed to load company currency");
    }
  }, []);

  useEffect(() => {
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else if (
      needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)
    ) {
      dispatch(
        setVisibleColumns({
          pageKey,
          columns: mergeVisibleColumns(
            preferences.visibleColumns,
            defaultVisibleColumns
          ),
        })
      );
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    if (!preferences.status) {
      dispatch(setFilterField({ pageKey, field: "status", value: defaultStatus }));
    }
    if (
      !preferences.pageSize ||
      !validPageSizes.includes(String(preferences.pageSize?.value))
    ) {
      dispatch(
        setFilterField({ pageKey, field: "pageSize", value: defaultPageSize })
      );
    }

    fetchCompCurrency();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageKey, fetchCompCurrency]);

  const fetchModifiersData = useCallback(async () => {
    const fetchSeq = ++fetchSeqRef.current;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_MODIFIERS, {
        params: {
          CompID,
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
          Search: search?.trim() || "",
          PageNumber: pageNumber,
          PageSize: pageSize.value,
          Status: status?.value !== "" ? String(status?.value) : "",
        },
      });

      if (fetchSeq !== fetchSeqRef.current) return;

      const modifiersData = response.data.data || response.data;
      const records = response.data.totalRecords || 0;

      setTotalPages(response.data.totalPages || 0);
      setTotalRecords(records);
      setModifiers(Array.isArray(modifiersData) ? modifiersData : []);

      const noSearch = !search.trim();
      const noStatus = status?.value === "" || status?.value === undefined;
      if (noSearch && noStatus) {
        applyCatalogTotalFromFetch(setCatalogTotalRecords, records);
      }
    } catch {
      if (fetchSeq !== fetchSeqRef.current) return;
      setModifiers([]);
      setTotalPages(0);
      setTotalRecords(0);
    } finally {
      if (fetchSeq === fetchSeqRef.current) {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    }
  }, [search, sortBy, sortOrder, CompID, pageNumber, pageSize, status]);

  useResetPageOnFilterChange(setPageNumber, [
    search,
    status?.value,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useInvalidateFetchOnFilterChange(fetchSeqRef, [
    search,
    status?.value,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useDebouncedEffect(() => fetchModifiersData(), [fetchModifiersData]);

  useEffect(() => {
    if (!initialLoadComplete) return;
    setLoading(true);
    // Intentionally omit initialLoadComplete from deps — including it re-triggers
    // setLoading(true) after the first fetch finishes and leaves the loader stuck.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    search,
    status?.value,
    pageNumber,
    pageSize?.value,
    sortBy?.value,
    sortOrder?.value,
  ]);

  const handleAddClick = () => {
    setEditingModifier(null);
    setIsFormModalOpen(true);
  };

  const handleEditClick = (modifier) => {
    setEditingModifier(modifier);
    setIsFormModalOpen(true);
  };

  const handleDeleteModifier = async (modifier) => {
    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${modifier.menuItem_Name}?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      const response = await axiosInstance.delete(APIs.DELETE_ITEM, {
        params: {
          CompID,
          MenuItem_ID: modifier.menuItem_ID,
        },
      });

      if (response.data.status) {
        showSuccessToast("Modifier is Deleted.");
        fetchModifiersData();
      } else {
        showErrorToast(response.data.message || "Deletion failed");
        setLoading(false);
      }
    } catch {
      showErrorToast("Something Went Wrong.");
      setLoading(false);
    }
  };

  const handleSelectItem = (itemId, index, event) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (event.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);
        for (let i = start; i <= end; i++) {
          if (modifiers[i]?.menuItem_ID) next.add(modifiers[i].menuItem_ID);
        }
      } else if (next.has(itemId)) {
        next.delete(itemId);
        lastSelectedIndex.current = index;
      } else {
        next.add(itemId);
        lastSelectedIndex.current = index;
      }
      return next;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedItems(new Set(modifiers.map((m) => m.menuItem_ID)));
    } else {
      setSelectedItems(new Set());
    }
    lastSelectedIndex.current = null;
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) {
      showErrorToast("No modifiers selected for deletion.");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${selectedItems.size} selected modifiers?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedItems).map((itemId) =>
          axiosInstance.delete(APIs.DELETE_ITEM, {
            params: { CompID, MenuItem_ID: itemId },
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      showSuccessToast(`${selectedItems.size} modifiers deleted successfully.`);
      fetchModifiersData();
    } catch {
      showErrorToast("Failed to delete selected modifiers.");
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearch("");
    dispatch(setFilterField({ pageKey, field: "status", value: defaultStatus }));
  };

  const handleColumnSort = useCallback(
    (sortField) => {
      const nextOrder =
        sortBy?.value === sortField && sortOrder?.value === "DESC"
          ? { value: "ASC", label: "ASC" }
          : { value: "DESC", label: "DESC" };

      dispatch(
        setSortBy({
          pageKey,
          sortBy: {
            value: sortField,
            label: MODIFIER_SORT_LABELS[sortField] ?? sortField,
          },
        })
      );
      dispatch(setSortOrder({ pageKey, sortOrder: nextOrder }));
    },
    [dispatch, sortBy?.value, sortOrder?.value]
  );

  const handleStatusBadgeClick = (item) => setStatusConfirmItem(item);
  const closeStatusConfirmModal = () => setStatusConfirmItem(null);

  const confirmStatusChange = async () => {
    if (!statusConfirmItem) return;
    const { menuItem_ID, menuItem_Publish, menuItem_Name } = statusConfirmItem;
    const newStatus = !menuItem_Publish;

    try {
      setModifiers((prev) =>
        prev.map((item) =>
          item.menuItem_ID === menuItem_ID
            ? { ...item, menuItem_Publish: newStatus }
            : item
        )
      );

      await axiosInstance.post(APIs.POST_UPDATE_ITEM_STATUS, {
        CompID,
        MenuItem_ID: menuItem_ID,
        MenuItem_Publish: newStatus,
      });

      showSuccessToast(
        `"${menuItem_Name}" is now ${newStatus ? "Active" : "InActive"}.`
      );
      closeStatusConfirmModal();
    } catch {
      setModifiers((prev) =>
        prev.map((item) =>
          item.menuItem_ID === menuItem_ID
            ? { ...item, menuItem_Publish }
            : item
        )
      );
      showErrorToast("Failed to update modifier status.");
    }
  };

  const hasFiltersApplied = () =>
    search.trim() !== "" || (status && status.value !== "");

  const showEmptyCatalogState =
    initialLoadComplete &&
    !loading &&
    !hasFiltersApplied() &&
    catalogTotalRecords === 0;

  const showGridToolbar = initialLoadComplete && !showEmptyCatalogState;

  const getEmptyStateDescription = () => {
    const parts = [];
    if (search.trim()) parts.push(`search "${search.trim()}"`);
    if (status?.value !== "" && status?.value !== undefined) {
      parts.push(`status "${status.label}"`);
    }
    if (parts.length === 0) {
      return "There are no modifiers to show right now. Add a new modifier or check back later.";
    }
    return `No modifiers match your current ${parts.join(", ")}. Try different filters or clear them to see all modifiers.`;
  };

  const renderBulkSelectionActions = () => {
    if (selectedItems.size === 0) return null;
    return (
      <div
        className="manage-items-bulk-actions"
        style={{ "--manage-items-theme": themeColor }}
      >
        <span className="manage-items-bulk-bar__count">
          {selectedItems.size} modifier
          {selectedItems.size > 1 ? "s" : ""} selected
        </span>
        <button
          type="button"
          className="manage-items-bulk-bar__btn manage-items-bulk-bar__btn--delete"
          onClick={handleBulkDelete}
          title="Delete Selected Modifiers"
        >
          <i className="fa fa-trash" aria-hidden="true" />
          Delete
        </button>
      </div>
    );
  };

  const renderNoDataMessage = () => {
    if (loading || modifiers.length > 0) return null;
    return (
      <GridEmptyState
        isDarkMode={isDarkMode}
        themeColor={themeColor}
        title="No modifiers found"
        description={getEmptyStateDescription()}
        onClearFilters={hasFiltersApplied() ? clearFilters : undefined}
        clearFiltersLabel="Clear all filters"
      />
    );
  };

  const formatPrice = (value) =>
    value === null || value === undefined ? "N/A" : Number(value).toFixed(2);

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Manage Modifiers</li>
          </ol>
          <h1 className="page-header mb-0">Manage Modifiers</h1>
        </div>
        <div className="ms-auto">
          <div className="d-flex justify-content-end align-items-center mb-3">
            <button
              className="btn px-4"
              onClick={handleAddClick}
              style={{
                backgroundColor: themeColor,
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                border: "none",
                color: "white",
                borderRadius: "8px",
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "600",
                transition: "all 0.3s ease",
                boxShadow: `0 2px 8px ${themeColor}40`,
              }}
              onMouseEnter={(e) => {
                e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                e.target.style.transform = "translateY(-1px)";
                e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
              }}
              onMouseLeave={(e) => {
                e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
              }}
            >
              <i className="fas fa-plus fa-lg me-2 ms-n2 text-white"></i>
              Add Modifier
            </button>
          </div>
        </div>
      </div>

      <div className="mb-3 mt-2">
        {!initialLoadComplete && (
          <GridSectionLoader loading minHeight="400px" />
        )}

        {showEmptyCatalogState && (
          <AddButton onClick={handleAddClick} title="Add Modifier" />
        )}

        {showGridToolbar && (
          <ModernGridToolbar
            isDarkMode={isDarkMode}
            title="Modifiers"
            totalCount={totalRecords}
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by name..."
            bodyFlush
            topFilters={
              <>
                <ModernGridToolbarFilterPill
                  iconClass="fa fa-filter"
                  className="modern-grid-toolbar__filter-pill--status"
                >
                  <Select
                    value={statusSelectValue}
                    onChange={(val) =>
                      dispatch(
                        setFilterField({
                          pageKey,
                          field: "status",
                          value: val,
                        })
                      )
                    }
                    options={statusOptions}
                    styles={filterPillSelectStyles}
                    placeholder="All Status"
                    isSearchable={false}
                    {...defaultSelectPortalProps}
                  />
                </ModernGridToolbarFilterPill>
                {renderBulkSelectionActions()}
              </>
            }
            headerControls={
              <ColumnSelection
                visibleColumns={visibleColumns}
                setVisibleColumns={(cols) =>
                  dispatch(setVisibleColumns({ pageKey, columns: cols }))
                }
                columnOrder={MODIFIER_GRID_COLUMN_ORDER}
              />
            }
          >
            <div className="px-3 pb-3">
              <GridSectionLoader loading={loading} minHeight="280px">
                {renderNoDataMessage()}

                {Array.isArray(modifiers) && modifiers.length > 0 && (
                  <div
                    className="table-responsive table-container"
                    style={{
                      minHeight: "200px",
                      maxHeight: "450px",
                      overflowY: "auto",
                      overflowX: "auto",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    <table
                      className="table table-hover"
                      style={{
                        position: "relative",
                        "--modern-grid-sort-active": themeColor,
                      }}
                    >
                      <thead
                        className="bg-light sticky-header"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 10000,
                          backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                          color: isDarkMode ? "#ffffff" : "#000000",
                        }}
                      >
                        <tr>
                          <th className="pt-2 pb-2" style={{ width: "48px" }}>
                            <input
                              type="checkbox"
                              onChange={handleSelectAll}
                              checked={
                                modifiers.length > 0 &&
                                selectedItems.size === modifiers.length
                              }
                            />
                          </th>
                          {visibleColumns.name && (
                            <SortableColumnHeader
                              sortField={MODIFIER_SORT_FIELDS.name}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                            >
                              Modifier
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.costPrice && (
                            <SortableColumnHeader
                              sortField={MODIFIER_SORT_FIELDS.costPrice}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                              className="text-center"
                            >
                              Cost Price ({getCurrencySymbol(compCurrency)})
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.tax && (
                            <SortableColumnHeader
                              sortField={MODIFIER_SORT_FIELDS.tax}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                              className="text-center"
                            >
                              Tax (%)
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.SalePrice && (
                            <SortableColumnHeader
                              sortField={MODIFIER_SORT_FIELDS.SalePrice}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                              className="text-center"
                            >
                              Price ({getCurrencySymbol(compCurrency)})
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.deliveryPrice && (
                            <SortableColumnHeader
                              sortField={MODIFIER_SORT_FIELDS.deliveryPrice}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                              className="text-center"
                            >
                              Delivery ({getCurrencySymbol(compCurrency)})
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.takeAwayPrice && (
                            <SortableColumnHeader
                              sortField={MODIFIER_SORT_FIELDS.takeAwayPrice}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                              className="text-center"
                            >
                              Take Away ({getCurrencySymbol(compCurrency)})
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.category && (
                            <th className="pt-2 pb-2">Category</th>
                          )}
                          {visibleColumns.stock && (
                            <SortableColumnHeader
                              sortField={MODIFIER_SORT_FIELDS.stock}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                              className="text-center"
                            >
                              Stock
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.status && (
                            <SortableColumnHeader
                              sortField={MODIFIER_SORT_FIELDS.status}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                              className="text-center"
                            >
                              Status
                            </SortableColumnHeader>
                          )}
                          <th className="pt-2 pb-2 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {modifiers.map((modifier, index) => (
                          <tr key={modifier.menuItem_ID}>
                            <td
                              className="align-middle"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={selectedItems.has(
                                  modifier.menuItem_ID
                                )}
                                onChange={(e) =>
                                  handleSelectItem(
                                    modifier.menuItem_ID,
                                    index,
                                    e
                                  )
                                }
                              />
                            </td>
                            {visibleColumns.name && (
                              <td
                                className="align-middle"
                                onClick={() => handleEditClick(modifier)}
                                style={{ cursor: "pointer" }}
                              >
                                {modifier.menuItem_Name}
                              </td>
                            )}
                            {visibleColumns.costPrice && (
                              <td
                                className="align-middle text-center"
                                onClick={() => handleEditClick(modifier)}
                                style={{ cursor: "pointer" }}
                              >
                                {formatPrice(modifier.menuItem_CostPrice)}
                              </td>
                            )}
                            {visibleColumns.tax && (
                              <td
                                className="align-middle text-center"
                                onClick={() => handleEditClick(modifier)}
                                style={{ cursor: "pointer" }}
                              >
                                {formatPrice(modifier.menuItem_TaxPercentage)}
                              </td>
                            )}
                            {visibleColumns.SalePrice && (
                              <td
                                className="align-middle text-center"
                                onClick={() => handleEditClick(modifier)}
                                style={{ cursor: "pointer" }}
                              >
                                {formatPrice(modifier.menuItem_SalePrice)}
                              </td>
                            )}
                            {visibleColumns.deliveryPrice && (
                              <td
                                className="align-middle text-center"
                                onClick={() => handleEditClick(modifier)}
                                style={{ cursor: "pointer" }}
                              >
                                {formatPrice(modifier.menuItem_DeliveryPrice)}
                              </td>
                            )}
                            {visibleColumns.takeAwayPrice && (
                              <td
                                className="align-middle text-center"
                                onClick={() => handleEditClick(modifier)}
                                style={{ cursor: "pointer" }}
                              >
                                {formatPrice(modifier.menuItem_TakeAwayPrice)}
                              </td>
                            )}
                            {visibleColumns.category && (
                              <td
                                className="align-middle"
                                onClick={() => handleEditClick(modifier)}
                                style={{ cursor: "pointer" }}
                              >
                                {modifier.rootCat_Name ||
                                  modifier.reportCat_Name ||
                                  "—"}
                              </td>
                            )}
                            {visibleColumns.stock && (
                              <td className="align-middle text-center">
                                <span
                                  className={`badge ${
                                    modifier.is_OutOfStock
                                      ? "bg-danger"
                                      : "bg-success"
                                  }`}
                                >
                                  {modifier.is_OutOfStock
                                    ? "Out of stock"
                                    : "In stock"}
                                </span>
                              </td>
                            )}
                            {visibleColumns.status && (
                              <td className="align-middle text-center">
                                <StatusBadge
                                  isActive={modifier.menuItem_Publish}
                                  onClick={() =>
                                    handleStatusBadgeClick(modifier)
                                  }
                                />
                              </td>
                            )}
                            <td className="align-middle text-center">
                              <TableActionsMenu>
                                <TableActionsItem
                                  icon="fa fa-edit"
                                  onClick={() => handleEditClick(modifier)}
                                >
                                  Edit
                                </TableActionsItem>
                                <TableActionsItem
                                  danger
                                  icon="fa fa-trash"
                                  onClick={() =>
                                    handleDeleteModifier(modifier)
                                  }
                                >
                                  Delete
                                </TableActionsItem>
                              </TableActionsMenu>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <ModernGridPagerFooter
                  pageSize={pageSize}
                  onPageSizeChange={handlePageSizeChange}
                  totalRecords={totalRecords}
                  pageNumber={pageNumber}
                  totalPages={totalPages}
                  setPageNumber={setPageNumber}
                />
              </GridSectionLoader>
            </div>
          </ModernGridToolbar>
        )}
      </div>

      <ModifierFormModal
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false);
          setEditingModifier(null);
        }}
        onSaved={fetchModifiersData}
        editingModifier={editingModifier}
        compCurrency={compCurrency}
      />

      <CustomModal
        title="Change modifier status?"
        subtitle={
          statusConfirmItem
            ? `Are you sure you want to set "${statusConfirmItem.menuItem_Name}" to ${
                statusConfirmItem.menuItem_Publish ? "InActive" : "Active"
              }?`
            : ""
        }
        fontSize="18px"
        fontWeight={400}
        onClose={closeStatusConfirmModal}
        isOpen={!!statusConfirmItem}
        isCentered={true}
        showButton={true}
        showSubmitButton={true}
        showCancelButton={true}
        submitButtonText="Confirm"
        onSubmit={confirmStatusChange}
        dialogMaxWidth="400px"
        minHeight="auto"
        maxHeight="320px"
      />
    </div>
  );
};

export default ManageModifiers;
